import { Link } from "react-router-dom";
import { FRANCHISE_TIERS } from "../lib/franchise";
import { Check, ArrowRight, Shield, TrendingUp, Building2, Users, Briefcase } from "lucide-react";

const VALUES = [
  { icon: Building2, title: "Your brand, our engine", body: "Custom domain. White-label palette. Your logo on every report. Our AI does the work — your name takes the credit." },
  { icon: Users, title: "Your clients, your revenue", body: "Set your own prices. Keep 100% of what you earn. We provide the infrastructure; you keep the practice." },
  { icon: Shield, title: "Fiduciary-grade frameworks", body: "Reg D, CDFI, SBDC, Reg A+ compliance playbooks baked in. You launch with the depth of an institution." },
  { icon: TrendingUp, title: "Compounding probabilities", body: "Risk-management discipline that scales with you — Tier 2 onward. Built so a single bad quarter never ends the practice." },
];

function TierCard({ tier }) {
  return (
    <div
      data-testid={`franchise-tier-${tier.id}`}
      className="relative flex flex-col p-10"
      style={{
        border: tier.featured ? "1px solid var(--brass)" : "1px solid var(--hairline)",
        background: tier.featured ? "var(--surface)" : "transparent",
      }}
    >
      {tier.badge && (
        <div className="absolute -top-3 left-10 px-3 py-1 text-[0.625rem] uppercase tracking-[0.22em]" style={{ background: "var(--bg)", color: "var(--brass)", border: "1px solid var(--brass)" }}>
          {tier.badge}
        </div>
      )}
      <p className="eyebrow mb-3">{tier.tagline}</p>
      <h3 className="font-display text-4xl mb-2">{tier.name}</h3>
      <p className="text-sm mb-6 leading-relaxed" style={{ color: "var(--ink-muted)" }}>{tier.headline}</p>
      <div className="flex items-baseline gap-2 mb-1">
        <span className="font-display text-5xl" style={{ color: "var(--brass)" }}>{tier.setupDisplay}</span>
        <span className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--ink-faint)" }}>setup</span>
      </div>
      <p className="text-sm mb-3" style={{ color: "var(--ink)" }}>
        + <span style={{ color: "var(--brass)", fontWeight: 600 }}>{tier.monthlyDisplay}</span>
      </p>
      <p className="text-xs uppercase tracking-[0.18em] mb-6" style={{ color: "var(--ink-muted)" }}>
        {tier.clientLimitDisplay}
      </p>
      <hr className="hairline mb-6" />
      <ul className="space-y-3 mb-10 flex-1">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm leading-relaxed">
            <Check size={14} className="mt-1 shrink-0" style={{ color: "var(--brass)" }} />
            <span style={{ color: "var(--ink)" }}>{f}</span>
          </li>
        ))}
      </ul>
      <Link
        to={`/franchise/checkout?tier=${tier.id}`}
        data-testid={`apply-franchise-${tier.id}`}
        className={tier.featured ? "btn-brass w-full" : "btn-ghost w-full"}
        style={{ justifyContent: "center" }}
      >
        {tier.cta} <ArrowRight size={14} />
      </Link>
    </div>
  );
}

export default function Franchise() {
  return (
    <div data-testid="franchise-page" className="pt-24">
      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-16">
        <div className="text-center max-w-4xl mx-auto reveal">
          <p className="eyebrow mb-5">Wealth First Inc. · Franchise Program</p>
          <h1 className="font-display text-6xl md:text-7xl lg:text-8xl mb-8 leading-[0.95]">
            Launch your own <em style={{ color: "var(--brass)" }}>white-label</em> <br />
            funding consultancy.
          </h1>
          <p className="text-lg md:text-xl leading-relaxed max-w-3xl mx-auto mb-10" style={{ color: "var(--ink-muted)" }}>
            Your brand. Your clients. Your revenue. Everything a serious partner needs to operate
            a business-funding consultancy under their own banner — backed by the WEALTH FIRST INC.
            AI engine, fiduciary frameworks, and lender network.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/franchise/checkout?tier=grow_rich" data-testid="franchise-hero-cta" className="btn-brass">
              Become a Grow-Rich Partner <ArrowRight size={14} />
            </Link>
            <Link to="/franchise/compare" className="btn-ghost">Compare Tiers</Link>
          </div>
        </div>
      </section>

      <hr className="hairline max-w-[1400px] mx-auto" />

      {/* VALUE PROPS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {VALUES.map((v) => {
            const Icon = v.icon;
            return (
              <div key={v.title} className="flex gap-5">
                <Icon size={28} style={{ color: "var(--brass)" }} className="shrink-0 mt-1" />
                <div>
                  <h3 className="font-display text-2xl mb-2">{v.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--ink-muted)" }}>{v.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <hr className="hairline max-w-[1400px] mx-auto" />

      {/* TIERS */}
      <section id="tiers" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="text-center mb-16">
          <p className="eyebrow mb-3">Three Franchise Tiers</p>
          <h2 className="font-display text-5xl md:text-6xl mb-6">Pick the practice you intend to build.</h2>
          <p className="text-base max-w-2xl mx-auto" style={{ color: "var(--ink-muted)" }}>
            Setup + monthly billing. Upgrade or scale at any time. Flexible payment plans available on Grow-Rich and Enterprise.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <TierCard tier={FRANCHISE_TIERS.starter} />
          <TierCard tier={FRANCHISE_TIERS.grow_rich} />
          <TierCard tier={FRANCHISE_TIERS.enterprise} />
        </div>

        <div className="text-center mt-12">
          <Link to="/franchise/compare" data-testid="franchise-compare-link" className="brass-link text-sm uppercase tracking-[0.22em]">
            View Full Comparison <ArrowRight size={12} className="inline ml-1" />
          </Link>
        </div>
      </section>

      <hr className="hairline max-w-[1400px] mx-auto" />

      {/* GROW-RICH FRAMEWORK CALLOUT */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-4">Grow-Rich · Risk Discipline</p>
            <h2 className="font-display text-5xl mb-6 leading-[0.95]">
              A franchise of <em style={{ color: "var(--brass)" }}>probabilities</em>.
            </h2>
            <Briefcase size={28} style={{ color: "var(--brass)" }} />
          </div>
          <div className="lg:col-span-7">
            <p className="text-lg leading-[1.85]" style={{ color: "var(--ink-muted)", fontFamily: "Cormorant Garamond" }}>
              A systematic process of identifying, assessing, and controlling threats to investment capital —
              <em style={{ color: "var(--brass)" }}> superior risk management</em> designed to ensure that no single event,
              nor series of events, can produce the catastrophic scenarios or market downturns that prevent further
              participation in an extreme volatile market.
            </p>
            <p className="text-lg leading-[1.85] mt-5" style={{ color: "var(--ink-muted)", fontFamily: "Cormorant Garamond" }}>
              By treating a franchise as a <em style={{ color: "var(--brass)" }}>business of probabilities</em>,
              risk management allows for consistent execution — even during periods of extreme uncertainty.
              This is the discipline Grow-Rich is built around. It is the discipline Enterprise inherits.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
